import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  images,
  ZENBU_OPTION,
} from '@constants';
import {
  TCreateSectorMasterReq,
  TFormModalRes,
  TListSectorTable,
  TUpdateSectorMasterReq,
} from '@models/sector';
import { masterDataApi } from '@services/masterDataApi';
import { sectorApi } from '@services/sectorApi';
import { handleShowToastError } from '@utils/helpers';
import { COMMON_CONSTANT, MODAL_TYPE } from '@utils/constants';
import { Form } from 'antd';
import { Table } from 'antd/lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EHttpStatusCode, TAxiosError } from 'types/service';
import { TGetCompanyParam, TGetSectorParam } from 'types/types';
import { getColumns } from '../columns';
import { SECTOR_PAGE_CONSTANT } from '../constant';

const { MESSAGE, FORM_MODAL, FORM_SEARCH } = SECTOR_PAGE_CONSTANT;

const MSG_KEY_UPDATE_SUCCESS_TITLE = 'COMMON.UPDATE_SUCCESS_TITLE';
const MSG_KEY_COMMON_CONFIRM = 'COMMON.COMMON_CONFIRM';

const CONFIRM_OR_DETAIL_MODAL_TYPES = [
  MODAL_TYPE.CONFIRM_REGISTRATION,
  MODAL_TYPE.CONFIRM_UPDATE,
  MODAL_TYPE.DETAIL,
];

const isSectorNameBlank = (sectorNameWatch: any) =>
  Boolean(sectorNameWatch && !sectorNameWatch.replace(/\s/g, '').length);

const isMissingRequiredSectorField = (
  sectorCodeWatch: any,
  companyIdWatch: any,
  sectorNameWatch: any
) =>
  !sectorCodeWatch ||
  !companyIdWatch ||
  !sectorNameWatch ||
  isSectorNameBlank(sectorNameWatch);

const getDisableBtnNext = (
  modalType: string,
  sectorCodeWatch: any,
  companyIdWatch: any,
  sectorNameWatch: any
) =>
  modalType !== MODAL_TYPE.DETAIL &&
  isMissingRequiredSectorField(
    sectorCodeWatch,
    companyIdWatch,
    sectorNameWatch
  );

const getDisableFields = (modalType: string) =>
  CONFIRM_OR_DETAIL_MODAL_TYPES.includes(modalType);

const getDisableFieldsEdit = (modalType: string, disableFields: boolean) =>
  modalType === MODAL_TYPE.UPDATE || disableFields;

function useSectorPage() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formModal] = Form.useForm();

  // Form data
  const sectorCodeWatch = Form.useWatch(
    FORM_MODAL.MODAL_SECTOR_CODE,
    formModal
  );
  const sectorNameWatch = Form.useWatch(
    FORM_MODAL.MODAL_SECTOR_NAME,
    formModal
  );
  const companyIdWatch = Form.useWatch(FORM_MODAL.MODAL_COMPANY_ID, formModal);

  // State
  const [loadingPage, setLoadingPage] = useState(true);
  const [listSector, setListSector] = useState<any>([]);
  const [companyDropdownData, setCompanyDropdownData] = useState<any>([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [companiesModal, setCompaniesModal] = useState<any>([]);
  const [sectorDropdownData, setSectorDropdownData] = useState<any>([]);
  const [currentSectorId, setCurrentSectorId] = useState('');
  const [modalDelete, setModalDelete] = useState(false);

  // Paging
  const [pageIndex, setPageIndex] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [commonSectorModal, setCommonSectorModal] = useState<{
    visible: boolean;
    modalType: string;
    title: string;
    image?: string;
    message?: string;
    disableField: boolean;
  }>({
    visible: false,
    modalType: '',
    title: '',
    disableField: false,
  });
  const [commonNotifyModal, setCommonNotifyModal] = useState<{
    visible: boolean;
    modalType: string;
    title: string;
    image?: string;
    message?: string;
    sectorIdRecord: string;
    sectorCode: string;
    sectorName: string;
  }>({
    visible: false,
    modalType: '',
    title: '',
    image: '',
    message: '',
    sectorIdRecord: '',
    sectorCode: '',
    sectorName: '',
  });

  const disableBtnNext = getDisableBtnNext(
    commonSectorModal.modalType,
    sectorCodeWatch,
    companyIdWatch,
    sectorNameWatch
  );

  const disableFields = getDisableFields(commonSectorModal.modalType);

  const isShowTooltip = disableFields;

  const disableFieldsEdit = getDisableFieldsEdit(
    commonSectorModal.modalType,
    disableFields
  );

  const tblRef: Parameters<typeof Table>[0]['ref'] = useRef(null);

  const handleSetFormValues = (record: TFormModalRes) => {
    formModal.setFieldValue(FORM_MODAL.MODAL_COMPANY_ID, record.companyId);
    formModal.setFieldValue(FORM_MODAL.MODAL_SECTOR_CODE, record.sectorCode);
    formModal.setFieldValue(FORM_MODAL.MODAL_SECTOR_NAME, record.sectorName);
  };

  // Modal registration
  const handleOpenModalRegistration = () => {
    setCommonSectorModal({
      visible: true,
      modalType: MODAL_TYPE.REGISTRATION,
      title: t('SECTOR_PAGE.SECTOR_REGISTRATION'),
      disableField: false,
    });
  };

  const handleOpenModalDetail = (record: TListSectorTable) => {
    setLoadingModal(true);

    setCommonSectorModal({
      visible: true,
      modalType: MODAL_TYPE.DETAIL,
      title: t('SECTOR_PAGE.SECTOR_DETAIL'),
      disableField: true,
    });

    setTimeout(() => {
      handleSetFormValues(record as any);
      setLoadingModal(false);
    }, 1000);
  };

  // Modal update
  const handleOpenModalUpdate = (record: TListSectorTable) => {
    setLoadingModal(true);
    setCurrentSectorId(record?.id);
    setCommonSectorModal({
      visible: true,
      modalType: MODAL_TYPE.UPDATE,
      title: t('SECTOR_PAGE.SECTOR_UPDATE'),
      disableField: false,
    });

    setTimeout(() => {
      handleSetFormValues(record as any);
      setLoadingModal(false);
    }, 1000);
  };

  const handleFormResetFieldError = () => {
    formModal.setFields([
      {
        name: [FORM_MODAL.MODAL_SECTOR_CODE],
        errors: [],
      },
      {
        name: [FORM_MODAL.MODAL_SECTOR_NAME],
        errors: [],
      },
    ]);
  };

  const handleValidateForm = () => {
    handleFormResetFieldError();
    let checkError = false;

    const validateField = (condition: boolean, fieldName: string) => {
      if (condition) {
        formModal.setFields([
          {
            name: [fieldName],
            errors: [t('COMMON_MSG.FIELD_INVALID_FORMAT')],
          },
        ]);
        checkError = true;
      }
    };

    validateField(
      sectorCodeWatch &&
        (sectorCodeWatch.length > 5 || !/^[0-9]+$/.test(sectorCodeWatch)),
      FORM_MODAL.MODAL_SECTOR_CODE
    );
    validateField(
      sectorNameWatch && sectorNameWatch.length > 50,
      FORM_MODAL.MODAL_SECTOR_NAME
    );

    if (!checkError) {
      if (commonSectorModal.modalType === MODAL_TYPE.REGISTRATION) {
        handleValidateSector({
          id: currentSectorId,
          companyId: companyIdWatch,
          sectorCode: sectorCodeWatch,
          sectorName: sectorNameWatch,
          isValidationOnly: true,
        });
      }

      if (commonSectorModal.modalType === MODAL_TYPE.UPDATE) {
        handleValidateSector({
          id: currentSectorId,
          companyId: companyIdWatch,
          sectorCode: sectorCodeWatch,
          sectorName: sectorNameWatch,
          isValidationOnly: true,
        });
      }
    }
  };

  // Modal delete sector
  const handleOpenModalDelete = (record: TListSectorTable) => {
    setCommonNotifyModal({
      visible: true,
      modalType: MODAL_TYPE.DELETE,
      title: t('COMMON.DELETE'),
      image: images.commonDelete,
      message: t('SECTOR_PAGE.CONFIRM_DELETE_SECTOR'),
      sectorIdRecord: record?.id,
      sectorCode: record?.sectorCode,
      sectorName: record?.sectorName,
    });
    setModalDelete(true);
  };

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollTop + clientHeight >= scrollHeight - 5 &&
      listSector.length < totalRecords
    ) {
      setPageIndex((prev) => prev + 1);
      handleGetSectorScroll({
        companyId: form.getFieldValue(FORM_SEARCH.COMPANY_ID),
        sectorId: form.getFieldValue(FORM_SEARCH.SECTOR_ID),
        page: pageIndex + 1,
      });
    }
  };

  // ======================= API ===============================
  const renderErrorMessageCreate = (errorCode: string) => {
    switch (errorCode) {
      case COMMON_CONSTANT.VALIDATE_SECTOR.SECTOR_ALREADY_EXISTED:
        return t('COMMON_MSG.DATA_EXIST');
      case COMMON_CONSTANT.VALIDATE_SECTOR.COMPANY_NOT_EXISTED:
        return t('SECTOR_PAGE.NOT_FOUND_COMPANY');
      default:
        return t('SECTOR_PAGE.FAIL_REGISTER');
    }
  };

  const renderErrorMessageUpdate = (errorCode: string) => {
    switch (errorCode) {
      case COMMON_CONSTANT.VALIDATE_SECTOR.SECTOR_ALREADY_EXISTED:
        return t('COMMON_MSG.DATA_EXIST');
      case COMMON_CONSTANT.VALIDATE_SECTOR.SECTOR_NOT_EXISTED:
        return t('SECTOR_PAGE.NOT_FOUND_SECTOR');
      default:
        return t('SECTOR_PAGE.UPDATE_FAIL');
    }
  };

  useEffect(() => {
    const fetchInitialApis = async (payload: TGetCompanyParam) => {
      let sectorData: { value: string; label: string }[] = [];
      try {
        const responseCompany = await masterDataApi.getCompany(payload);
        if (responseCompany.ok && responseCompany.data.length) {
          const companyData = responseCompany.data.map((val) => ({
            value: val.id,
            label: val.companyCode + '_' + val.companyName,
          }));
          setCompanyDropdownData([ZENBU_OPTION, ...companyData]);
          setCompaniesModal(companyData);
          form.setFieldValue(FORM_SEARCH.COMPANY_ID, companyData[0].value);
          const responseSector = await masterDataApi.getSector({
            companyId: companyData[0].value,
            storeId: '',
          });
          if (responseSector.ok && responseSector.data.length) {
            sectorData = responseSector.data.map((val) => ({
              value: val.id,
              label: val.sectorCode + '_' + val.sectorName,
            }));
            setSectorDropdownData([ZENBU_OPTION, ...sectorData]);
            form.setFieldValue(FORM_SEARCH.SECTOR_ID, sectorData[0].value);
          }
          await handleGetSectorsSearch({
            companyId: form.getFieldValue(FORM_SEARCH.COMPANY_ID),
            sectorId: form.getFieldValue(FORM_SEARCH.SECTOR_ID),
            page: DEFAULT_PAGE_NUMBER,
            pageSize: DEFAULT_PAGE_SIZE,
          });
        }
      } catch (error) {
        handleShowToastError(error, '');
      } finally {
        setLoadingPage(false);
      }
    };

    fetchInitialApis({ storeId: '' });
  }, []);

  const handleDeleteSector = async (payload: string) => {
    try {
      setLoadingPage(true);
      const response = await sectorApi.deleteSector(payload);
      if (response.ok) {
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.SUCCESS_DELETE,
          title: t(MSG_KEY_UPDATE_SUCCESS_TITLE),
          image: images.confirmApproveTemperature,
          message: t('SECTOR_PAGE.SUCCESS_DELETE'),
          sectorIdRecord: '',
          sectorCode: '',
          sectorName: '',
        });
        handleGetSectorsSearch({
          companyId: form.getFieldValue(FORM_SEARCH.COMPANY_ID),
          sectorId: form.getFieldValue(FORM_SEARCH.SECTOR_ID),
          page: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_PAGE_SIZE,
        });
      }
    } catch (error) {
      const err = error as TAxiosError;
      const errorCode = err?.data?.errors?.[0]?.code;
      if (errorCode) {
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.FAIL_DELETE,
          title: t(MSG_KEY_COMMON_CONFIRM),
          image: images.importDataFail,
          message:
            errorCode ===
            COMMON_CONSTANT.VALIDATE_SECTOR.SECTOR_HAS_LINKED_STORES
              ? t('SECTOR_PAGE.SECTOR_HAS_LINKED_STORES')
              : errorCode === COMMON_CONSTANT.VALIDATE_SECTOR.SECTOR_NOT_EXISTED
              ? t('SECTOR_PAGE.NOT_FOUND_SECTOR')
              : t('SECTOR_PAGE.FAIL_DELETE'),
          sectorIdRecord: '',
          sectorName: '',
          sectorCode: '',
        });
        return;
      }
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const handleUpdateSector = async (payload: TUpdateSectorMasterReq) => {
    try {
      setLoadingPage(true);
      const response = await sectorApi.updateSector(payload);
      if (response.ok) {
        setCommonSectorModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.SUCCESS_UPDATE,
          title: t(MSG_KEY_UPDATE_SUCCESS_TITLE),
          image: images.confirmApproveTemperature,
          message: t('SECTOR_PAGE.SUCCESS_UPDATE'),
          sectorIdRecord: '',
          sectorCode: '',
          sectorName: '',
        });
        handleGetSectorsSearch({
          companyId: form.getFieldValue(FORM_SEARCH.COMPANY_ID),
          sectorId: form.getFieldValue(FORM_SEARCH.SECTOR_ID),
          page: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_PAGE_SIZE,
          sectorCode: 0,
        });
      }
    } catch (error: any) {
      const err = error as TAxiosError;
      const errorCode = err.data?.errors?.[0]?.code;
      if (errorCode) {
        setCommonSectorModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.FAIL_DELETE,
          title: t(MSG_KEY_COMMON_CONFIRM),
          image: images.importDataFail,
          message: renderErrorMessageUpdate(errorCode),
          sectorIdRecord: '',
          sectorName: '',
          sectorCode: '',
        });
        return;
      }
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const handleCreateSector = async (payload: TCreateSectorMasterReq) => {
    try {
      setLoadingPage(true);
      const response = await sectorApi.createSector(payload);
      if (response.ok) {
        setCommonSectorModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.SUCCESS_REGISTRATION,
          title: t(MSG_KEY_UPDATE_SUCCESS_TITLE),
          image: images.confirmApproveTemperature,
          message: t('SECTOR_PAGE.SUCCESS_REGISTER'),
          sectorIdRecord: '',
          sectorCode: '',
          sectorName: '',
        });
        handleGetSectorsSearch({
          companyId: form.getFieldValue(FORM_SEARCH.COMPANY_ID),
          sectorId: form.getFieldValue(FORM_SEARCH.SECTOR_ID),
          page: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_PAGE_SIZE,
          sectorCode: 0,
        });
      }
    } catch (error: any) {
      const err = error as TAxiosError;
      const errorCode = err.data?.errors?.[0]?.code;
      if (errorCode) {
        setCommonSectorModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.FAIL_REGISTRATION,
          title: t(MSG_KEY_COMMON_CONFIRM),
          image: images.importDataFail,
          message: renderErrorMessageCreate(errorCode),
          sectorIdRecord: '',
          sectorCode: '',
          sectorName: '',
        });
        return;
      }
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const handleValidateSector = async (payload: any) => {
    try {
      if (commonSectorModal.modalType === MODAL_TYPE.UPDATE) {
        const response = await sectorApi.updateSector(payload);
        if (response.ok) {
          setCommonSectorModal({
            visible: true,
            modalType: MODAL_TYPE.CONFIRM_UPDATE,
            disableField: true,
            title: t('SECTOR_PAGE.SECTOR_UPDATE'),
          });
        }
      } else {
        const response = await sectorApi.createSector(payload);
        if (response.ok) {
          setCommonSectorModal({
            visible: true,
            modalType: MODAL_TYPE.CONFIRM_REGISTRATION,
            disableField: true,
            title: t('SECTOR_PAGE.SECTOR_REGISTRATION'),
          });
        }
      }
    } catch (error: any) {
      if (
        error.status === EHttpStatusCode.BAD_REQUEST &&
        error?.data?.errors?.[0]?.code ===
          COMMON_CONSTANT.VALIDATE_SECTOR.SECTOR_ALREADY_EXISTED
      ) {
        formModal.setFields([
          {
            name: [FORM_MODAL.MODAL_SECTOR_CODE],
            errors: [MESSAGE.DATA_EXIST],
          },
        ]);
        return;
      }
      if (
        error.status === EHttpStatusCode.BAD_REQUEST &&
        error?.data?.errors?.[0]?.code ===
          COMMON_CONSTANT.VALIDATE_SECTOR.SECTOR_NOT_EXISTED
      ) {
        setCommonSectorModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.FAIL_UPDATE,
          title: t(MSG_KEY_COMMON_CONFIRM),
          image: images.importDataFail,
          message: t('COMMON_MSG.DATA_NOT_EXIST'),
          sectorIdRecord: '',
          sectorCode: '',
          sectorName: '',
        });
        return;
      }
      handleShowToastError(error, '');
    }
  };

  const handleGetSectors = async (payload: TGetSectorParam) => {
    try {
      const response = await masterDataApi.getSector(payload as any);
      if (response.ok && response.data.length) {
        const sectorData = response.data.map((val) => ({
          value: val.id,
          label: val.sectorCode + '_' + val.sectorName,
        }));

        setSectorDropdownData([ZENBU_OPTION, ...sectorData]);
        form.setFieldValue(
          FORM_SEARCH.SECTOR_ID,
          payload.companyId ? sectorData[0].value : ''
        );
      }
    } catch (error) {
      handleShowToastError(error, '');
    }
  };

  const handleGetSectorsSearch = async (payload: any) => {
    setLoadingPage(true);
    try {
      const response = await sectorApi.getSector(payload);
      if (response.ok) {
        setListSector(
          response.data.map((item: any, index: number) => ({
            ...item,
            no: index + 1,
          }))
        );
        setPageIndex(1);
        setTotalRecords(response.pagination?.totalCount as number);
      }
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const handleGetSectorScroll = async (payload: any) => {
    setLoadingPage(true);
    try {
      const response = await sectorApi.getSector(payload);
      const newListSector = [...listSector, ...response.data].map(
        (item, index) => ({
          ...item,
          no: index + 1,
        })
      );
      setListSector(newListSector);
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };
  // =====================================================

  const handleChangeCompany = (val: any) => {
    form.setFieldValue(FORM_SEARCH.SECTOR_ID, '');
    setSectorDropdownData([]);
    if (val) {
      handleGetSectors({ companyId: val, storeId: '' });
    } else {
      handleGetSectors({ companyId: '', storeId: '' });
    }
  };

  const handleFinalAction = () => {
    setLoadingPage(true);
    const params = {
      companyId: companyIdWatch,
      sectorCode: sectorCodeWatch,
      sectorName: sectorNameWatch,
      isValidationOnly: false,
    };

    if (commonNotifyModal.modalType === MODAL_TYPE.CONFIRM_REGISTRATION_FINAL) {
      handleCreateSector({
        companyId: params.companyId,
        sectorName: params.sectorName,
        sectorCode: params.sectorCode,
        isValidationOnly: false,
      });
    }

    if (commonNotifyModal.modalType === MODAL_TYPE.CONFIRM_UPDATE_FINAL) {
      handleUpdateSector({
        id: currentSectorId,
        sectorName: params.sectorName,
        sectorCode: params.sectorCode,
        isValidationOnly: false,
      });
    }

    if (commonNotifyModal.modalType === MODAL_TYPE.DELETE) {
      handleDeleteSector(commonNotifyModal?.sectorIdRecord);
    }
  };

  // Modal confirm final
  const handleOpenModalConfirmFinal = () => {
    if (commonSectorModal.modalType === MODAL_TYPE.CONFIRM_REGISTRATION) {
      setCommonNotifyModal({
        ...commonSectorModal,
        modalType: MODAL_TYPE.CONFIRM_REGISTRATION_FINAL,
        title: t(MSG_KEY_COMMON_CONFIRM),
        image: images.confirmImage,
        message: t('SECTOR_PAGE.CONFIRM_REGIS_SECTOR'),
        sectorIdRecord: '',
        sectorCode: '',
        sectorName: '',
      });
    }
    if (commonSectorModal.modalType === MODAL_TYPE.CONFIRM_UPDATE) {
      setCommonNotifyModal({
        ...commonSectorModal,
        modalType: MODAL_TYPE.CONFIRM_UPDATE_FINAL,
        title: t(MSG_KEY_COMMON_CONFIRM),
        image: images.confirmImage,
        message: t('SECTOR_PAGE.CONFIRM_UPDATE_SECTOR'),
        sectorIdRecord: '',
        sectorCode: '',
        sectorName: '',
      });
    }
  };

  const columns = useMemo(() => {
    const columns = getColumns({
      handleOpenModalDelete,
      handleOpenModalDetail,
      handleOpenModalUpdate,
    });
    return columns;
  }, []);

  const handleClearSearch = () => {
    form?.resetFields();
    form.setFieldValue(FORM_SEARCH.COMPANY_ID, '');
    form.setFieldValue(FORM_SEARCH.SECTOR_ID, '');

    handleGetSectors({
      companyId: form.getFieldValue(FORM_SEARCH.COMPANY_ID),
      storeId: '',
    });
  };

  const handleSearch = (values: any) => {
    tblRef.current?.scrollTo({ top: 0 });
    setPageIndex(1);
    handleGetSectorsSearch({
      ...values,
      page: 0,
    });
  };

  return {
    state: {
      columns,
      listSector,
      loadingPage,
      tblRef,
      form,
      disableBtnNext,
      disableFields,
      disableFieldsEdit,
      loadingModal,
      formModal,
      modalDelete,
      commonSectorModal,
      commonNotifyModal,
      companyDropdownData,
      companiesModal,
      sectorDropdownData,
      isShowTooltip,
    },
    handler: {
      handleScroll,
      handleOpenModalRegistration,
      handleValidateForm,
      handleOpenModalConfirmFinal,
      setCommonNotifyModal,
      setCommonSectorModal,
      handleFinalAction,
      handleClearSearch,
      handleGetSectors,
      handleChangeCompany,
      handleSearch,
    },
  };
}

export default useSectorPage;
